/**
 * CoveragePanel — live staffing coverage for a shift.
 *
 * Confirmed counts reflect REAL talent confirmations only (a booked worker
 * who has not personally confirmed is shown as "Awaiting confirmation", never
 * as confirmed). Data comes from GET /api/shift-confirmation/coverage/{shiftId}.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Users, ShieldCheck } from 'lucide-react';
import api from '../../utils/api';

const Stat = ({ label, value, tone = 'slate', testid }) => {
  const tones = {
    slate: 'text-slate-900',
    green: 'text-green-600',
    amber: 'text-amber-600',
    rose: 'text-rose-600',
  };
  return (
    <div className="flex flex-col items-center justify-center rounded-lg bg-white border border-slate-200 py-2 px-1">
      <div className={`text-xl font-bold leading-none ${tones[tone] || tones.slate}`} data-testid={testid}>
        {value}
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mt-1 text-center leading-tight">
        {label}
      </div>
    </div>
  );
};

export default function CoveragePanel({ shiftId, refreshKey, compact = false }) {
  const [cov, setCov] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCov = useCallback(async () => {
    if (!shiftId) return;
    setLoading(true);
    try {
      const res = await api.get(`/shift-confirmation/coverage/${shiftId}`);
      setCov(res.data);
    } catch {
      setCov(null);
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => { fetchCov(); }, [fetchCov, refreshKey]);

  if (loading && !cov) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500" data-testid="coverage-panel-loading">
        Loading coverage…
      </div>
    );
  }
  if (!cov) return null;

  const isUrgent = cov.coverage_status === 'urgent_coverage_needed';
  const isNeeded = cov.coverage_status === 'coverage_needed';
  const secured = cov.coverage_secured;

  const bannerClass = isUrgent
    ? 'bg-rose-600 text-white'
    : isNeeded
      ? 'bg-amber-500 text-white'
      : secured
        ? 'bg-green-600 text-white'
        : 'bg-slate-600 text-white';

  const bannerLabel = isUrgent
    ? 'URGENT COVERAGE NEEDED'
    : isNeeded
      ? 'Coverage Needed'
      : secured
        ? 'Fully Staffed'
        : (cov.coverage_status === 'closed' ? 'Shift Closed' : 'Coverage');

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="coverage-panel">
      <div className={`flex items-center justify-between px-3 py-2 ${bannerClass}`}>
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide" data-testid="coverage-status-badge">
          {isUrgent ? <AlertTriangle className="w-4 h-4" />
            : secured ? <ShieldCheck className="w-4 h-4" />
              : <Users className="w-4 h-4" />}
          {bannerLabel}
        </div>
        <div className="flex items-center gap-1 text-[11px] font-semibold">
          {secured
            ? <><CheckCircle2 className="w-3.5 h-3.5" /> Coverage secured</>
            : <><Clock className="w-3.5 h-3.5" /> Coverage at risk</>}
        </div>
      </div>

      <div className={`grid ${compact ? 'grid-cols-4' : 'grid-cols-4 sm:grid-cols-5'} gap-1.5 p-2 bg-slate-50`}>
        <Stat label="Required" value={cov.required} testid="coverage-required" />
        <Stat label="Booked" value={cov.primary_booked} testid="coverage-booked" />
        <Stat label="Confirmed" value={cov.primary_confirmed}
              tone={cov.primary_confirmed >= cov.required ? 'green' : 'slate'} testid="coverage-confirmed" />
        <Stat label="Awaiting" value={cov.awaiting_confirmation}
              tone={cov.awaiting_confirmation > 0 ? 'amber' : 'slate'} testid="coverage-awaiting" />
        <Stat label="Coverage need" value={cov.positions_needing_coverage}
              tone={cov.positions_needing_coverage > 0 ? 'rose' : 'green'} testid="coverage-needed-count" />
      </div>

      {cov.backup_required > 0 && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-white border-t border-slate-100 text-[11px] text-slate-600" data-testid="coverage-backups">
          <span className="font-semibold">Backups</span>
          <span>{cov.backup_confirmed} confirmed / {cov.backup_required} required · {cov.backup_booked} assigned</span>
        </div>
      )}
    </div>
  );
}
